import React, { useEffect, useRef, useState } from 'react';
import { useParams, useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { Alert, Box, Paper, Stack, Typography, Divider, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Button, TextField, Backdrop, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Collapse, Chip } from '@mui/material';
import ApproveRejectButtons from '../components/common/ApproveRejectButtons';
import PRPostingReviewDialog from '../components/common/PRPostingReviewDialog';
import apiService from '../services/api.service';
import { useDispatch, useSelector } from 'react-redux';
import { showSnackbar } from '../store/slices/uiSlice';
import AddIcon from '@mui/icons-material/Add';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { prLineValues } from '../utils/prLineValues';

const toDDMMYYYY = (v: any): string => {
  const s = String(v || '').trim();
  if (!s) return '';
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}-${iso[2]}-${iso[1]}`;
  const slash = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (slash) return `${slash[1]}-${slash[2]}-${slash[3]}`;
  const compact = s.match(/^(\d{8})$/);
  if (compact) return `${s.slice(6,8)}-${s.slice(4,6)}-${s.slice(0,4)}`;
  const d = new Date(s);
  if (!isNaN(d.getTime())) {
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  }
  return s;
};

const PR_CLASSIC = {
  blue: '#0070f2',
  blueDark: '#0057d2',
  bluePale: '#dbe8f6',
  yellow: '#ffc107',
  yellowDark: '#856404',
  yellowPale: '#fff4d6',
  ink: '#1d2d3e',
  muted: '#6c757d',
  border: '#758ca4',
  canvas: '#f5f6f7',
};

const PRDetail: React.FC = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const navType = useNavigationType();
  const dispatch = useDispatch();
  const user = useSelector((s: any) => s.auth.user);
  const [data, setData] = useState<any | null>(null);
  const [activeDetailTab, setActiveDetailTab] = useState('pr-b1-overview');
  const [loading, setLoading] = useState(false);
  const [workflow, setWorkflow] = useState<any | null>(null);
  const [attachments, setAttachments] = useState<any[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [comment, setComment] = useState<string>('');
  const [actionProcessing, setActionProcessing] = useState<'approve'|'reject'|'return'|null>(null);
  const processingRef = useRef<boolean>(false);
  const [reopenDialog, setReopenDialog] = useState(false);
  const [reopenReason, setReopenReason] = useState('');
  const [reopening, setReopening] = useState(false);
  const [postingReviewOpen, setPostingReviewOpen] = useState(false);
  const [poProcessing, setPoProcessing] = useState(false);
  const [moveProcessing, setMoveProcessing] = useState(false);
  const [navigating, setNavigating] = useState(false);
  const [debugOpen, setDebugOpen] = useState(false);
  const [sapErrorDebug, setSapErrorDebug] = useState<any | null>(null);
  const [sapPostDebugAttempts, setSapPostDebugAttempts] = useState<any[]>([]);
  const [debugPanelOpen, setDebugPanelOpen] = useState(false);
  const [postingHistory, setPostingHistory] = useState<any[]>([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [approvalHistory, setApprovalHistory] = useState<any[]>([]);
  const refreshTimerRef = useRef<any>(null);
  const sseRef = useRef<EventSource | null>(null);

  const isAuthorizedApprover = (() => {
    const ids = Array.isArray((workflow as any)?.pendingApproverUserIds) ? (workflow as any).pendingApproverUserIds : [];
    const uid = Number((user as any)?.userid || 0);
    return !!uid && ids.includes(uid);
  })();

  useEffect(() => {
    const initial = (location.state as any)?.pr;
    if (initial) {
      setData(initial);
    }
  }, [location.state]);

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const r = await apiService.getPR(Number(id));
        const d = (r as any)?.data || r || {};
        setData(d);
        try {
          const [wfRes, attRes] = await Promise.all([
            apiService.getWorkflowStatus(Number(id)),
            apiService.getPRAttachments(Number(id))
          ]);
          const wf = (wfRes as any)?.data || wfRes || null;
          const att = (attRes as any)?.data || attRes || [];
          setWorkflow(wf);
          setAttachments(Array.isArray(att) ? att : []);
          try {
            const apprHistRes = await apiService.getApprovalHistory(Number(id));
            const apprHist = (apprHistRes as any)?.data || apprHistRes || [];
            setApprovalHistory(Array.isArray(apprHist) ? apprHist : []);
          } catch {
            setApprovalHistory([]);
          }
          try {
            const histRes = await apiService.getPRPostingAttempts(Number(id));
            const hist = (histRes as any)?.data || histRes || [];
            setPostingHistory(Array.isArray(hist) ? hist : []);
          } catch { setPostingHistory([]); }
        } catch {
          setWorkflow(null);
          setAttachments([]);
          setApprovalHistory([]);
          setPostingHistory([]);
        }
      } catch {
        setData(null);
        setWorkflow(null);
        setAttachments([]);
        setApprovalHistory([]);
        setPostingHistory([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, user]);

  const statusVal = String((data as any)?.status || '');
  useEffect(() => {
    if (!id) return;
    const pending = /pending/i.test(statusVal);
    if (pending && !refreshTimerRef.current && !sseRef.current) {
      refreshTimerRef.current = setInterval(async () => {
        try {
          const r = await apiService.getPR(Number(id));
          const d = (r as any)?.data || r || {};
          setData(d);
          try {
            const wfRes = await apiService.getWorkflowStatus(Number(id));
            const wf = (wfRes as any)?.data || wfRes || null;
            setWorkflow(wf);
            try {
              const apprHistRes = await apiService.getApprovalHistory(Number(id));
              const apprHist = (apprHistRes as any)?.data || apprHistRes || [];
              setApprovalHistory(Array.isArray(apprHist) ? apprHist : []);
            } catch {}
            try {
              const phRes = await apiService.getPRPostingAttempts(Number(id));
              const ph = (phRes as any)?.data || phRes || [];
              setPostingHistory(Array.isArray(ph) ? ph : []);
            } catch {}
          } catch {}
        } catch {}
      }, 5000);
    }
    if (!pending && refreshTimerRef.current) {
      try { clearInterval(refreshTimerRef.current); } catch {}
      refreshTimerRef.current = null;
    }
    return () => {
      if (refreshTimerRef.current) {
        try { clearInterval(refreshTimerRef.current); } catch {}
        refreshTimerRef.current = null;
      }
    };
  }, [id, statusVal]);

  useEffect(() => {
    if (!id) return;
    try {
      const es = apiService.subscribePRStatus(Number(id));
      sseRef.current = es as EventSource;
      es.onmessage = async (e: MessageEvent) => {
        try {
          const payload = JSON.parse(String(e.data || '{}'));
          if (payload && typeof payload === 'object') {
            if (payload.pr) setData((prev: any) => ({ ...(prev || {}), ...(payload.pr || {}) }));
            if (payload.workflow) setWorkflow(payload.workflow);
            const t = String(payload.type || '');
            if (t && (/^sap-pr-created$/i.test(t) || /^sap-posting-attempt$/i.test(t))) {
              try {
                const phRes = await apiService.getPRPostingAttempts(Number(id));
                const ph = (phRes as any)?.data || phRes || [];
                setPostingHistory(Array.isArray(ph) ? ph : []);
              } catch {}
            }
          }
        } catch {}
      };
      es.onerror = () => {};
    } catch {}
    return () => {
      try {
        if (sseRef.current) sseRef.current.close();
      } catch {}
      sseRef.current = null;
    };
  }, [id]);

  const refreshAttachments = async () => {
    if (!id) return;
    try {
      const attRes = await apiService.getPRAttachments(Number(id));
      const att = (attRes as any)?.data || attRes || [];
      setAttachments(Array.isArray(att) ? att : []);
    } catch {
      setAttachments([]);
    }
  };

  const onUploadClick = () => {
    fileInputRef.current?.click();
  };

  const onFilesSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!id) return;
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (!files.length) return;
    setUploading(true);
    try {
      await apiService.uploadPRAttachments(Number(id), files);
      await refreshAttachments();
    } catch {
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleApprove = async () => {
    if (!id) return;
    if (processingRef.current) return;
    try {
      processingRef.current = true;
      setActionProcessing('approve');
      setLoading(true);
      const approvalResult: any = await apiService.processApproval(Number(id), 'Approved', comment.trim());
      dispatch(showSnackbar({ message: approvalResult?.message || `PR ${id} approved`, severity: ['Failed', 'Unknown'].includes(approvalResult?.data?.sappostingstatus) ? 'warning' : 'success' }));
      try { window.dispatchEvent(new CustomEvent('pr-updated', { detail: { id: Number(id), status: 'Approved' } })); } catch {}
      const r = await apiService.getPR(Number(id));
      const d = (r as any)?.data || r || {};
      setData(d);
      try {
        const wfRes = await apiService.getWorkflowStatus(Number(id));
        const wf = (wfRes as any)?.data || wfRes || null;
        setWorkflow(wf);
        try {
          const apprHistRes = await apiService.getApprovalHistory(Number(id));
          const apprHist = (apprHistRes as any)?.data || apprHistRes || [];
          setApprovalHistory(Array.isArray(apprHist) ? apprHist : []);
        } catch {}
        try {
          const phRes = await apiService.getPRPostingAttempts(Number(id));
          const ph = (phRes as any)?.data || phRes || [];
          setPostingHistory(Array.isArray(ph) ? ph : []);
        } catch {}
      } catch {
        setWorkflow(null);
      }
    } catch {
      dispatch(showSnackbar({ message: `Approval failed`, severity: 'error' }));
    } finally {
      setLoading(false);
      setActionProcessing(null);
      processingRef.current = false;
    }
  };

  const handleReject = async () => {
    if (!id) return;
    if (processingRef.current) return;
    if (!comment.trim()) {
      dispatch(showSnackbar({ message: 'Please enter a rejection reason', severity: 'warning' }));
      return;
    }
    try {
      processingRef.current = true;
      setActionProcessing('reject');
      setLoading(true);
      await apiService.processApproval(Number(id), 'Rejected', comment.trim());
      dispatch(showSnackbar({ message: `PR ${id} rejected`, severity: 'success' }));
      try { window.dispatchEvent(new CustomEvent('pr-updated', { detail: { id: Number(id), status: 'Rejected' } })); } catch {}
      const r = await apiService.getPR(Number(id));
      const d = (r as any)?.data || r || {};
      setData(d);
      try {
        const wfRes = await apiService.getWorkflowStatus(Number(id));
        const wf = (wfRes as any)?.data || wfRes || null;
        setWorkflow(wf);
        try {
          const apprHistRes = await apiService.getApprovalHistory(Number(id));
          const apprHist = (apprHistRes as any)?.data || apprHistRes || [];
          setApprovalHistory(Array.isArray(apprHist) ? apprHist : []);
        } catch {}
      } catch {
        setWorkflow(null);
      }
    } catch {
      dispatch(showSnackbar({ message: `Rejection failed`, severity: 'error' }));
    } finally {
      setLoading(false);
      setActionProcessing(null);
      processingRef.current = false;
    }
  };

  const handleReturn = async () => {
    if (!id) return;
    if (processingRef.current) return;
    if (!comment.trim()) {
      dispatch(showSnackbar({ message: 'Please enter a return comment', severity: 'warning' }));
      return;
    }
    try {
      processingRef.current = true;
      setActionProcessing('return');
      setLoading(true);
      await apiService.processApproval(Number(id), 'Returned', comment.trim());
      dispatch(showSnackbar({ message: `PR ${id} reverted back`, severity: 'success' }));
      try { window.dispatchEvent(new CustomEvent('pr-updated', { detail: { id: Number(id), status: 'Returned' } })); } catch {}
      const r = await apiService.getPR(Number(id));
      const d = (r as any)?.data || r || {};
      setData(d);
      try {
        const wfRes = await apiService.getWorkflowStatus(Number(id));
        const wf = (wfRes as any)?.data || wfRes || null;
        setWorkflow(wf);
        try {
          const apprHistRes = await apiService.getApprovalHistory(Number(id));
          const apprHist = (apprHistRes as any)?.data || apprHistRes || [];
          setApprovalHistory(Array.isArray(apprHist) ? apprHist : []);
        } catch {}
      } catch {
        setWorkflow(null);
      }
    } catch {
      dispatch(showSnackbar({ message: `Revert failed`, severity: 'error' }));
    } finally {
      setLoading(false);
      setActionProcessing(null);
      processingRef.current = false;
    }
  };

  const handleCreatePO = async () => {
    if (!id) return;
    if (poProcessing) return;
    try {
      setPoProcessing(true);
      setLoading(true);
      const r = await apiService.createPOFromPR(Number(id));
      const d = (r as any)?.data || r || {};
      try {
        const role = String(user?.role || '');
        const allowedModules: string[] = (((user as any)?.allowedmodules ?? []) as string[]);
        const canSeeSAPDebug = role === 'Admin' || role === 'ProcurementOfficer' || allowedModules.includes('post-sap');
        const dbg = (r as any)?.debug;
        if (canSeeSAPDebug && Array.isArray(dbg) && dbg.length) {
          setSapPostDebugAttempts(dbg);
          setDebugPanelOpen(true);
        } else {
          setSapPostDebugAttempts([]);
        }
      } catch {}
      const posArr = Array.isArray((d as any).pos) ? (d as any).pos : null;
      const isPR = String((d as any).docType || '').toUpperCase() === 'PR';
      if (isPR) {
        dispatch(showSnackbar({ message: `Purchase Request created: DocNum ${d.docNum || ''} / DocEntry ${d.docEntry || ''}`, severity: 'success' }));
      } else if (posArr && posArr.length) {
        const summary = posArr.map((p: any) => `${String(p.vendorCode || '')}#${p.docNum || p.docEntry || ''}`).join(', ');
        dispatch(showSnackbar({ message: `POs created: ${summary}`, severity: 'success' }));
      } else {
        dispatch(showSnackbar({ message: `PO created: DocNum ${d.docNum || ''} / DocEntry ${d.docEntry || ''}`, severity: 'success' }));
      }
      const prRes = await apiService.getPR(Number(id));
      setData((prRes as any)?.data || prRes || {});
      try {
        const wfRes = await apiService.getWorkflowStatus(Number(id));
        setWorkflow((wfRes as any)?.data || wfRes || null);
      } catch {}
      try {
        const phRes = await apiService.getPRPostingAttempts(Number(id));
        const ph = (phRes as any)?.data || phRes || [];
        setPostingHistory(Array.isArray(ph) ? ph : []);
      } catch {}
    } catch (e: any) {
      const raw = (e?.response?.data as any) || {};
      const actual = raw?.message || raw?.error?.message?.value || e?.message || 'Failed to post to SAP';
      dispatch(showSnackbar({ message: actual, severity: 'error' }));
      try {
        const role = String(user?.role || '');
        const allowedModules: string[] = (((user as any)?.allowedmodules ?? []) as string[]);
        const canSeeSAPDebug = role === 'Admin' || role === 'ProcurementOfficer' || allowedModules.includes('post-sap');
        if (canSeeSAPDebug && raw?.debug) {
          const dbg = raw.debug;
          if (Array.isArray(dbg) && dbg.length) {
            setSapPostDebugAttempts(dbg);
            setDebugPanelOpen(true);
          } else {
            setSapErrorDebug(dbg);
            setDebugOpen(true);
          }
        }
      } catch {}
      try {
        const phRes = await apiService.getPRPostingAttempts(Number(id));
        const ph = (phRes as any)?.data || phRes || [];
        setPostingHistory(Array.isArray(ph) ? ph : []);
      } catch {}
    } finally {
      setLoading(false);
      setPoProcessing(false);
    }
  };

  const handleMoveToCompleted = async () => {
    if (!id) return;
    if (moveProcessing) return;
    try {
      setMoveProcessing(true);
      setLoading(true);
      await apiService.movePRToCompleted(Number(id));
      dispatch(showSnackbar({ message: `PR ${id} moved to Completed`, severity: 'success' }));
      try { window.dispatchEvent(new CustomEvent('pr-updated', { detail: { id: Number(id), inboxStatus: 'Completed' } })); } catch {}
      const r = await apiService.getPR(Number(id));
      const d = (r as any)?.data || r || {};
      setData(d);
      try {
        const wfRes = await apiService.getWorkflowStatus(Number(id));
        const wf = (wfRes as any)?.data || wfRes || null;
        setWorkflow(wf);
      } catch {
        setWorkflow(null);
      }
    } catch (e: any) {
      const raw = (e?.response?.data as any) || {};
      const actual = raw?.message || e?.message || 'Failed to move to Completed';
      dispatch(showSnackbar({ message: actual, severity: 'error' }));
    } finally {
      setLoading(false);
      setMoveProcessing(false);
    }
  };

  const goBack = () => {
    if (loading || actionProcessing) return;
    setNavigating(true);
    try {
      if (navType === 'PUSH') {
        navigate(-1);
      } else {
        navigate((user as any)?.allowedmodules?.includes('pr-dashboard') ? '/pr-dashboard' : '/dashboard');
      }
    } finally {
      setTimeout(() => setNavigating(false), 600);
    }
  };

  const goDashboard = () => {
    if (loading || actionProcessing) return;
    setNavigating(true);
    try {
      navigate((user as any)?.allowedmodules?.includes('pr-dashboard') ? '/pr-dashboard' : '/dashboard');
    } finally {
      setTimeout(() => setNavigating(false), 600);
    }
  };

  const getWorkflowSteps = (): any[] => {
    const baseRaw = Array.isArray((workflow as any)?.steps) ? (workflow as any).steps : [];

    // Deduplicate steps by step number (in case backend returns duplicates)
    const stepMap = new Map<number, any>();
    for (const step of baseRaw) {
      const stepNum = Number(step.stepNumber || 0);
      if (stepNum > 0 && !stepMap.has(stepNum)) {
        stepMap.set(stepNum, step);
      }
    }
    const base = Array.from(stepMap.values()).sort((a, b) => Number(a.stepNumber || 0) - Number(b.stepNumber || 0));

    let steps = base.length ? base : (() => {
      const type = String((data as any)?.requisitiontype || '').trim();
      const alloc = String((data as any)?.allocationtype || '').trim();
      const roles: string[] = [];
      if ((type === 'Inventory' || type === 'Service') && alloc === 'Project') {
        roles.push('ProjectManager', 'OperationsManager');
      } else if ((type === 'Inventory' || type === 'Service') && alloc === 'CostCenter') {
        roles.push('CostCenterManager');
      } else if (type === 'FixedAsset' && alloc === 'Project') {
        roles.push('ProjectManager', 'OperationsManager', 'FinanceManager', 'GeneralManager');
      } else if (type === 'FixedAsset' && alloc === 'CostCenter') {
        roles.push('CostCenterManager', 'FinanceManager', 'GeneralManager');
      }
      const cur = Number((data as any)?.currentstep || 0);
      return roles.map((role, idx) => ({
        stepNumber: idx + 1,
        approverRole: role,
        approverName: '',
        approverEmail: '',
        status: (idx + 1) === cur ? 'In Approval' : 'Pending',
        actionDate: null,
        comments: ''
      }));
    })();
    const finalStepNo = (steps[steps.length - 1]?.stepNumber ?? steps.length) + 1;
    const attemptsFlat = ([] as any[]).concat(...(postingHistory || []).map((g: any) => (g.attempts || [])));
    const prAttempts = attemptsFlat
      .filter((a: any) => String(a.endpoint || '') === '/PurchaseRequests' || String(a.docType || '').toUpperCase() === 'PR')
      .sort((a: any, b: any) => String(b.postedAt || '').localeCompare(String(a.postedAt || '')));
    const lastAttempt = prAttempts.length ? prAttempts[0] : null;
    const headerPosted = /PostedToSAP\/PR/i.test(String((data as any)?.status || '')) || (Number((data as any)?.sappodocentry || 0) > 0) || (Number((data as any)?.sappodocnum || 0) > 0) || (Array.isArray((data as any)?.poRefs) && ((data as any)?.poRefs as any[]).some((p: any) => String((p?.doctype ?? p?.DocType) || '').toUpperCase() === 'PR' && (Number((p?.docentry ?? p?.DocEntry) || 0) > 0 || Number((p?.docnum ?? p?.DocNum) || 0) > 0)));
    const status = data?.sappostingstatus === 'Failed' ? 'Failed' : ['Unknown', 'Posting'].includes(data?.sappostingstatus) ? 'Requires verification' : data?.reinitiationrouting ? 'Pending' : lastAttempt ? ((String(lastAttempt.outcome || '').toLowerCase() === 'success' && String(lastAttempt.docType || '').toUpperCase() === 'PR') ? 'Completed' : (String(lastAttempt.outcome || '').toLowerCase() === 'failure' ? 'Failed' : 'Pending')) : (headerPosted ? 'Completed' : 'Pending');
    const actionDate = lastAttempt ? (lastAttempt.postedAt || null) : null;
    const approverName = lastAttempt ? String(lastAttempt.postedByName || '') : '';
    const comments = data?.sappostingerror || (lastAttempt && String(lastAttempt.errorMessage || '').trim() ? String(lastAttempt.errorMessage || '') : '');
    steps = steps.concat([{ stepNumber: finalStepNo, approverRole: 'Post to SAP', approverName, approverEmail: '', status, actionDate, comments }]);
    return steps;
  };

  const poRelationRows = Array.isArray((data as any)?.poRefs)
    ? ((data as any).poRefs as any[]).filter((p: any) => {
        const docType = String((p?.doctype ?? p?.DocType) || '').toUpperCase();
        return Number(p?.poid || 0) > 0 || docType === 'PO' || String(p?.reftype || '').toLowerCase() === 'copied';
      })
    : [];

  const prSapRows = Array.isArray((data as any)?.poRefs)
    ? ((data as any).poRefs as any[]).filter((p: any) => {
        const docType = String((p?.doctype ?? p?.DocType) || '').toUpperCase();
        return docType === 'PR' || (Number(p?.poid || 0) <= 0 && (Number(p?.docnum || 0) > 0 || Number(p?.docentry || 0) > 0));
      })
    : [];

  const exportApprovalHistory = async () => {
    if (!id) return;
    try {
      const r = await apiService.getApprovalHistory(Number(id));
      const rows = ((r as any)?.data || r || []) as any[];
      let finalStepNo = 0;
      try {
        const steps = getWorkflowSteps();
        finalStepNo = (steps[steps.length - 1]?.stepNumber ?? steps.length);
      } catch {}
      const headers = ['StepNumber','ApproverName','ApproverEmail','ApproverRole','Action','ActionDate','Comments'];
      const baseLines = rows.map((h) => [h.stepnumber, h.approvername, h.approveremail, h.approverrole, h.action, h.actiondate, (String(h.comments||'').replace(/\r?\n/g,' '))].map(String).join(','));
      let postLines: string[] = [];
      try {
        const ph = await apiService.getPRPostingAttempts(Number(id));
        const groups = ((ph as any)?.data || ph || []) as any[];
        const attempts = ([] as any[])
          .concat(...groups.map((g: any) => (g.attempts || [])))
          .filter((a: any) => String(a.endpoint || '') === '/PurchaseRequests' || String(a.docType || '').toUpperCase() === 'PR');
        postLines = attempts.map((a: any) => [
          finalStepNo,
          String(a.postedByName || ''),
          '',
          'Post to SAP',
          (String(a.outcome || '').toLowerCase() === 'success' && String(a.docType || '').toUpperCase() === 'PR') ? 'PostedToSAP/PR' : (String(a.outcome || '').toLowerCase() === 'failure' ? 'PostingFailed' : 'PostAttempt'),
          String(a.postedAt || ''),
          String(((a.docNum || a.docEntry) ? (`DocNum ${String(a.docNum || '')} / DocEntry ${String(a.docEntry || '')}`) : '') + (String(a.errorMessage || '').trim() ? ( (a.docNum || a.docEntry) ? ' ' : '' ) + String(a.errorMessage || '').replace(/\r?\n/g,' ') : ''))
        ].map(String).join(','));
      } catch {}
      const lines = [headers.join(',')].concat(baseLines).concat(postLines);
      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `PR-${id}-ApprovalHistory.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      dispatch(showSnackbar({ message: 'Approval history exported', severity: 'success' }));
    } catch {
      dispatch(showSnackbar({ message: 'Failed to export approval history', severity: 'error' }));
    }
  };

  const isServiceModule = String(data?.requisitiontype || '').trim().toLowerCase() === 'service';
  const isAdminUser = String(user?.role || '') === 'Admin';

  const reloadAfterPostingReview = async (message: string) => {
    setPostingReviewOpen(false);
    dispatch(showSnackbar({ message, severity: 'success' }));
    try {
      const r = await apiService.getPR(Number(id));
      setData((r as any)?.data || r || {});
    } catch {}
    try {
      const histRes = await apiService.getPRPostingAttempts(Number(id));
      const hist = (histRes as any)?.data || histRes || [];
      setPostingHistory(Array.isArray(hist) ? hist : []);
    } catch {}
    try { window.dispatchEvent(new CustomEvent('pr-updated', { detail: { id: Number(id) } })); } catch {}
  };

  return (
    <>
    {isAdminUser && !!id && (
      <PRPostingReviewDialog open={postingReviewOpen} prId={Number(id)} onClose={() => setPostingReviewOpen(false)} onResolved={(message) => { void reloadAfterPostingReview(message); }} />
    )}
    <Dialog open={reopenDialog} onClose={() => { if (!reopening) setReopenDialog(false); }} maxWidth="sm" fullWidth>
      <DialogTitle>Edit &amp; reinitiate PR</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>The PR will become a draft. Edit its details, then submit to restart the same approval flow from stage 1. Previous details and approvals are retained. Company, type, project/cost center, facility and cycle stay unchanged.</Typography>
        <TextField label="Reason for correction" value={reopenReason} onChange={e => setReopenReason(e.target.value)} multiline minRows={3} fullWidth inputProps={{ maxLength: 1000 }} disabled={reopening} />
      </DialogContent>
      <DialogActions>
        <Button disabled={reopening} onClick={() => setReopenDialog(false)}>Cancel</Button>
        <Button variant="contained" disabled={reopening || reopenReason.trim().length < 5} onClick={async () => {
          setReopening(true);
          try {
            await apiService.reinitiatePR(Number(id), reopenReason.trim());
            navigate('/create-pr?editPR=' + Number(id));
          } catch (error: any) {
            dispatch(showSnackbar({ severity: 'error', message: error?.response?.data?.message || 'Unable to reopen PR.' }));
          } finally { setReopening(false); }
        }}>{reopening ? 'Opening…' : 'Open for correction'}</Button>
      </DialogActions>
    </Dialog>
    <Paper elevation={0} sx={{
      p: { xs: 1, sm: 2, md: 2.5 }, width: '100%', minWidth: 0, overflowX: 'hidden',
      bgcolor: PR_CLASSIC.canvas, color: PR_CLASSIC.ink, border: `1px solid ${PR_CLASSIC.border}`, borderRadius: { xs: 1.5, sm: 2.5 },
      boxShadow: '0 4px 18px rgba(22, 49, 78, .12)',
      '& .MuiTableContainer-root': { bgcolor: '#fff', border: '1px solid #c8d4e2', borderRadius: 2, boxShadow: '0 2px 7px rgba(23, 50, 79, .08)', overflow: 'auto' },
      '& .MuiTableCell-root': { borderColor: '#e2e7ed', verticalAlign: 'top' },
      '& .MuiTableHead-root .MuiTableCell-root': { background: 'linear-gradient(180deg,#e9f1fb 0%,#cad9eb 100%)', color: '#123f70', fontWeight: 700, borderBottom: '2px solid #0070f2' },
      '& .MuiTableBody-root .MuiTableRow:nth-of-type(even)': { bgcolor: '#f6f9fc' },
      '& .MuiTableBody-root .MuiTableRow:hover': { bgcolor: '#eaf1fb' },
      '& .MuiButton-containedPrimary': { bgcolor: PR_CLASSIC.blue, color: '#fff', boxShadow: 'none', '&:hover': { bgcolor: PR_CLASSIC.blueDark, boxShadow: 'none' } },
      '& .MuiButton-outlined': { borderColor: PR_CLASSIC.blue, color: PR_CLASSIC.blueDark, '&:hover': { borderColor: PR_CLASSIC.blue, bgcolor: PR_CLASSIC.bluePale } },
    }}>
      {['Failed', 'Unknown', 'Posting'].includes(data?.sappostingstatus) && (
        <Alert severity={data.sappostingstatus === 'Failed' ? 'error' : 'warning'} sx={{ mb: 2 }}>
          <Typography fontWeight={700}>{data.sappostingstatus === 'Failed' ? 'Final approved — SAP posting failed' : 'Final approved — SAP posting requires verification'}</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{data.sappostingerror || 'SAP posting has not been confirmed.'}</Typography>
          <Typography variant="body2">{data.sappostingstatus === 'Failed' ? 'Correct the PR and resubmit to restart approvals, or ask procurement to address the SAP issue.' : isAdminUser ? 'Check SAP for this PR, then link the document SAP created or confirm that none exists. Editing and reinitiation stay blocked until then to prevent duplicates.' : 'Contact your administrator to check SAP before retrying. Editing and reinitiation are blocked to prevent duplicates.'}</Typography>
          {isAdminUser && ['Unknown', 'Posting'].includes(data.sappostingstatus) && <Button sx={{ mt: 1 }} color="inherit" variant="outlined" onClick={() => setPostingReviewOpen(true)}>Review SAP posting</Button>}
          {data.sappostingstatus === 'Failed' && data.status === 'Approved' && Number(data.requestedbyuserid) === Number(user?.userid) && !data.convertedtopo && !Number(data.sappodocentry) && !Number(data.sappodocnum) && data.approvalrouting?.stages?.length > 0 && <Button sx={{ mt: 1 }} color="inherit" variant="outlined" onClick={() => setReopenDialog(true)}>Edit &amp; reinitiate</Button>}
        </Alert>
      )}
      {data?.reinitiationrouting && <Alert severity="info" sx={{ mb: 2 }}>This PR is open for correction. Previous approvals remain in the history. <Button onClick={() => navigate('/create-pr?editPR=' + Number(id))}>Continue editing</Button></Alert>}
      <Stack direction={{ xs: 'column', lg: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', lg: 'center' }} mb={0} spacing={1.5} sx={{ p: { xs: 1.25, sm: 1.5 }, bgcolor: PR_CLASSIC.bluePale, backgroundImage: 'linear-gradient(135deg,#c7dcf3 0%,#eaf2fb 58%,#fff 100%)', border: '1px solid #9fb9d6', borderBottom: `3px solid ${PR_CLASSIC.blue}`, borderRadius: '10px 10px 0 0', boxShadow: 'inset 0 1px 0 rgba(255,255,255,.9)' }}>
        <Stack direction="row" spacing={1} alignItems="center" minWidth={0}>
          <Box sx={{ width: 34, height: 34, borderRadius: 1, display: 'grid', placeItems: 'center', flexShrink: 0, bgcolor: PR_CLASSIC.blue, color: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,.18)' }}><ReceiptLongIcon fontSize="small" /></Box>
          <Stack spacing={0.25} minWidth={0}>
            <Typography variant="h6" sx={{ fontWeight: 750, color: PR_CLASSIC.blueDark, lineHeight: 1.2, letterSpacing: '-0.01em' }}>Purchase Requisition</Typography>
            {data?.prnumber ? <Chip size="small" label={`PR ${data.prnumber}`} sx={{ alignSelf: 'flex-start', height: 20, bgcolor: PR_CLASSIC.yellowPale, border: '1px solid #d8bd62', borderRadius: '3px', color: '#6d5700', fontWeight: 700 }} /> : null}
          </Stack>
        </Stack>
        <Stack direction="column" spacing={1} alignItems={{ xs: 'stretch', lg: 'flex-end' }}>
          <Stack direction="row" spacing={1} alignItems="center" useFlexGap sx={{ flexWrap: 'wrap' }}>
            {(user as any)?.allowedmodules?.includes('create-pr') && <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/create-pr')} disabled={loading}>New PR</Button>}
            <Button size="small" variant="outlined" onClick={goBack} disabled={loading}>Back</Button>
            <Button size="small" variant="outlined" onClick={goDashboard} disabled={loading}>{(user as any)?.allowedmodules?.includes('pr-dashboard') ? 'PR Dashboard' : 'Dashboard'}</Button>
            {(() => {
            const isInitiator = Number((user as any)?.userid || 0) === Number((data as any)?.requestedbyuserid || 0);
            const isRejected = String((data as any)?.status || '').toLowerCase() === 'rejected';
            if (!(isInitiator && isRejected)) return null;
            return (
              <Button
                size="small"
                variant="contained"
                color="warning"
                onClick={handleMoveToCompleted}
                disabled={loading || moveProcessing}
                startIcon={moveProcessing ? <CircularProgress size={16} /> : undefined}
              >
                {moveProcessing ? 'Moving…' : 'Move to Completed'}
              </Button>
            );
          })()}
          {(() => {
            const allowedModules: string[] = (((user as any)?.allowedmodules ?? []) as string[]);
            const hasPostingModule = allowedModules.includes('post-sap');
            const canPost = !!data && String(data.status) === 'Approved' && !['Posting', 'Unknown', 'Posted'].includes(data.sappostingstatus) && !Number(data.sappodocentry) && !Number(data.sappodocnum) && !data.convertedtopo && (String(user?.role) === 'ProcurementOfficer' || String(user?.role) === 'Admin' || hasPostingModule);
            if (!canPost) return null;
            return (
              <Button
                size="small"
                variant="contained"
                color="secondary"
                onClick={handleCreatePO}
                disabled={loading || poProcessing}
                startIcon={poProcessing ? <CircularProgress size={16} /> : undefined}
              >
                {poProcessing ? 'Posting…' : 'Post To SAP'}
              </Button>
            );
          })()}
          {(() => {
            const isActionable = !!data && /pending/i.test(String(data.status || ''));
            const canAct = isActionable && isAuthorizedApprover;
          return (
            <ApproveRejectButtons
              onReturn={handleReturn}
              onReject={handleReject}
              onApprove={handleApprove}
              rejectDisabled={!!actionProcessing || loading || !canAct}
              approveDisabled={!!actionProcessing || loading || !canAct}
              returnDisabled={!!actionProcessing || loading || !canAct}
            />
          );
        })()}
          {(() => {
            const showWarn = !!data && /pending/i.test(String(data.status || '')) && !isAuthorizedApprover;
            return showWarn ? (
              <Typography variant="caption" color="error" sx={{ maxWidth: 360 }}>You are not an assigned reviewer for the current approval stage.</Typography>
            ) : null;
          })()}
          </Stack>
          <TextField
            size="small"
            label="Comment"
            placeholder="Add a note for approve/reject/revert"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            multiline
            minRows={2}
            sx={{ width: { xs: '100%', sm: 360 } }}
            disabled={!!actionProcessing || loading}
          />
          {!!data && /pending/i.test(String(data.status || '')) && isAuthorizedApprover ? (
            <Typography variant="caption" color="text.secondary">Add a comment when returning or rejecting this PR.</Typography>
          ) : null}
        </Stack>
      </Stack>

      {!data ? (
        <Typography variant="body2">{loading ? 'Loading...' : 'PR not found'}</Typography>
      ) : (
        <Box>
          <Box className="pr-b1-tabs" sx={{ display: 'flex', gap: 0.25, overflowX: 'auto', whiteSpace: 'nowrap', borderBottom: `2px solid ${PR_CLASSIC.blueDark}`, mt: 0, mb: 1.5, px: 0.5 }}>
            {[
              ['Overview', 'pr-b1-overview'],
              ['Approval Flow', 'pr-b1-workflow'],
              ['Attachments', 'pr-b1-attachments'],
              ['Lines', 'pr-b1-lines'],
              ['G/L Impact', 'pr-b1-gl'],
            ].map(([label, target]) => <Button key={target} size="small" onClick={() => {
              setActiveDetailTab(target);
              document.getElementById(target)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }} sx={{ flexShrink: 0, borderRadius: '4px 4px 0 0', px: 1.4, py: 0.55, minHeight: 30, fontSize: '.72rem', fontWeight: 700, color: activeDetailTab === target ? PR_CLASSIC.blueDark : '#526170', bgcolor: activeDetailTab === target ? '#fff' : '#e8edf3', border: '1px solid #b6c6db', borderBottom: activeDetailTab === target ? '2px solid #fff' : 'none', '&:hover': { bgcolor: PR_CLASSIC.bluePale } }}>{label}</Button>)}
          </Box>
          <Box id="pr-b1-overview" sx={{ scrollMarginTop: 72, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' }, gap: 1, mb: 1.5, p: 1.25, bgcolor: '#e8edf4', border: '1px solid #bccbdd', borderRadius: 2, boxShadow: 'inset 0 1px 2px rgba(24,52,83,.06)' }}>
            {[
              { label: 'PR Type', value: data.requisitiontype || '-' },
              { label: String(data.allocationtype || '').toLowerCase() === 'project' ? 'Selected Project' : String(data.allocationtype || '').toLowerCase() === 'costcenter' ? 'Selected Cost Center' : 'Allocation', value: `${(data as any).allocationDetails?.code || data.allocationcode || data.lines?.find((line: any) => line.projectcode || line.ProjectCode)?.projectcode || data.lines?.find((line: any) => line.projectcode || line.ProjectCode)?.ProjectCode || '-'}${(data as any).allocationDetails?.name ? ` · ${(data as any).allocationDetails.name}` : ''}`, emphasis: true },
              { label: 'Approval Routing', value: (data as any).approvalrouting ? `${(data as any).approvalrouting.source} — ${(data as any).approvalrouting.flowName}${(data as any).approvalrouting.version ? ' · v' + (data as any).approvalrouting.version : ''}` : (data as any).approvaltemplateid ? `Template #${(data as any).approvaltemplateid} (existing PR)` : 'Legacy / unpinned (existing PR)' },
              { label: 'Needed By', value: toDDMMYYYY((data as any).requireddate) || '-' },
            ].map((item) => <Paper key={item.label} variant="outlined" sx={{ p: 1.5, minWidth: 0, borderRadius: 1.5, bgcolor: item.emphasis ? PR_CLASSIC.yellowPale : '#fff', borderColor: item.emphasis ? '#d8bd62' : '#c8d4e2', borderLeft: item.emphasis ? `4px solid ${PR_CLASSIC.yellow}` : '3px solid #8fa9c5', boxShadow: '0 2px 6px rgba(22,49,78,.08)' }}>
              <Typography variant="caption" sx={{ color: PR_CLASSIC.muted, display: 'block', mb: 0.5, textTransform: 'uppercase', letterSpacing: '.045em', fontWeight: 700, fontSize: '.64rem' }}>{item.label}</Typography>
              <Typography variant="body2" fontWeight={700} sx={{ overflowWrap: 'anywhere', color: item.emphasis ? '#705900' : PR_CLASSIC.ink }}>{item.value}</Typography>
            </Paper>)}
            <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1, bgcolor: '#fff', borderColor: '#d3d9e1', borderLeft: '3px solid #d3d9e1' }}>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Status</Typography>
              <Chip size="small" label={data.sappostingstatus === 'Failed' ? 'Final approved — SAP posting failed' : data.status || 'Unknown'} color={/approved|completed|posted/i.test(String(data.status)) ? 'success' : /reject|return|error/i.test(String(data.status)) ? 'error' : /pending|submit/i.test(String(data.status)) ? 'warning' : 'default'} />
            </Paper>
            {((data as any).sappodocentry || (data as any).sappodocnum) ? (
              <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1, bgcolor: '#fff', borderColor: '#d3d9e1', borderLeft: '3px solid #d3d9e1' }}><Typography variant="caption" color="text.secondary" display="block" mb={0.5}>SAP Document</Typography><Typography variant="body2" fontWeight={700}>{(/PostedToSAP\/PR/i.test(String(data.status || '')) ? 'PR' : 'PO')} {((data as any).sappodocnum || '-')}</Typography></Paper>
            ) : null}
            <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1, bgcolor: PR_CLASSIC.yellowPale, borderColor: '#d8bd62', borderLeft: `4px solid ${PR_CLASSIC.yellow}` }}><Typography variant="caption" color="text.secondary" display="block" mb={0.5}>PR Total</Typography><Typography variant="body1" fontWeight={800} color="#705900">{(isServiceModule ? (data.lines || []).reduce((sum: number, line: any) => sum + prLineValues(data.requisitiontype, line).amount, 0) : Number(data.totalamount || 0)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {data.currency || ''}</Typography></Paper>
          </Box>
          <Stack spacing={0.5} mb={2}>
            {(data as any).remarks || data.remarks ? (
              <Box sx={{ mt: 1, p: 1.25, bgcolor: PR_CLASSIC.yellowPale, border: '1px solid #d8bd62', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: PR_CLASSIC.ink, mb: 0.5 }}>
                  Initiator Remarks:
                </Typography>
                <Typography variant="body2" sx={{ color: '#333', whiteSpace: 'pre-wrap' }}>
                  {(data as any).remarks || data.remarks}
                </Typography>
              </Box>
            ) : null}
          </Stack>

          <Divider sx={{ my: 2 }} />
          <Stack id="pr-b1-workflow" direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1, scrollMarginTop: 72 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700, color: PR_CLASSIC.blueDark, borderLeft: `3px solid ${PR_CLASSIC.blue}`, pl: 1, textTransform: 'uppercase', letterSpacing: '.035em', fontSize: '.82rem' }}>Workflow</Typography>
            <Button size="small" variant="outlined" onClick={exportApprovalHistory} disabled={loading}>Export Approval History</Button>
          </Stack>
          <TableContainer sx={{ overflowX: 'auto', mb: 2 }}><Table size="small" sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600 }}>Step</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Assigned Approver(s)</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Approved By</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Decided At</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Comments</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {getWorkflowSteps().map((s: any, i: number) => {
                const isCurrent = Number(s.stepNumber) === Number((data as any)?.currentstep || 0);
                const hasAction = !!s.actionDate;
                let displayStatus = String(s.status || '');
                if (!hasAction) {
                  if (isCurrent && displayStatus === 'Pending') displayStatus = 'In Approval';
                  else if (displayStatus === 'Skipped') displayStatus = 'Pending';
                }
                if (displayStatus === 'Returned') displayStatus = 'Revert Back';

                // Get approver names from approval history for this step (who actually acted)
                let actionedBy = '';
                if (String(s.approverRole || '') !== 'Post to SAP') {
                  const historyRows = (approvalHistory || []).filter((h: any) => Number(h.stepnumber) === Number(s.stepNumber) && (!data?.approvalrestartedat || new Date(h.actiondate).getTime() >= new Date(data.approvalrestartedat).getTime()));
                  if (historyRows.length > 0) {
                    actionedBy = historyRows.map((h: any) => h.approvername).filter(Boolean).join(', ');
                  }
                } else if (s.approverName) {
                  actionedBy = s.approverName;
                }

                return (
                  <TableRow key={i}>
                    <TableCell>{s.stepNumber}</TableCell>
                    <TableCell>{s.approverRole}</TableCell>
                    <TableCell>{s.approverName || ''}</TableCell>
                    <TableCell>{actionedBy || ''}</TableCell>
                    <TableCell>{displayStatus}</TableCell>
                    <TableCell>{toDDMMYYYY(s.actionDate)}</TableCell>
                    <TableCell>
                      {(() => {
                        if (String(s.approverRole || '') === 'Post to SAP') {
                          const attempts = ([] as any[])
                            .concat(...(postingHistory || []).map((g: any) => (g.attempts || [])))
                            .filter((a: any) => String(a.endpoint || '') === '/PurchaseRequests' || String(a.docType || '').toUpperCase() === 'PR')
                            .sort((a: any, b: any) => String(b.postedAt || '').localeCompare(String(a.postedAt || '')));
                          const latest = attempts.length ? attempts[0] : null;
                          if (!latest) return s.comments || '';
                          const hasError = String(latest.errorMessage || '').trim().length > 0;
                          const isSuccess = String(latest.outcome || '').toLowerCase() === 'success' && String(latest.docType || '').toUpperCase() === 'PR';
                          return (
                            <Stack spacing={0.5}>
                              {hasError ? (
                                <Typography variant="caption">{String(latest.errorMessage || '')}</Typography>
                              ) : null}
                              {(!hasError && isSuccess) ? (
                                <Typography variant="caption">{`Posted to SAP PR — DocNum ${String(latest.docNum || '')} / DocEntry ${String(latest.docEntry || '')}`}</Typography>
                              ) : null}
                            </Stack>
                          );
                        }
                        const rows = (approvalHistory || []).filter((h: any) => Number(h.stepnumber) === Number(s.stepNumber) && (!data?.approvalrestartedat || new Date(h.actiondate).getTime() >= new Date(data.approvalrestartedat).getTime()));
                        if (!rows.length) return s.comments || '';
                        return (
                          <Stack spacing={0.5}>
                            {rows.map((h: any, idx: number) => (
                              <Typography key={idx} variant="caption">
                                {`${toDDMMYYYY(h.actiondate)} • ${h.approvername || ''} • ${h.action === 'Returned' ? 'Revert Back' : h.action}${String(h.comments || '').trim() ? ' – ' + String(h.comments || '') : ''}`}
                              </Typography>
                            ))}
                          </Stack>
                        );
                      })()}
                    </TableCell>
                  </TableRow>
                )
              })}
              {(getWorkflowSteps().length === 0) && (
                <TableRow>
                  <TableCell colSpan={7}>No workflow data</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table></TableContainer>

          <Divider sx={{ my: 2 }} />
          <Stack id="pr-b1-attachments" direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1, scrollMarginTop: 72 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700, color: PR_CLASSIC.blueDark, borderLeft: `3px solid ${PR_CLASSIC.blue}`, pl: 1, textTransform: 'uppercase', letterSpacing: '.035em', fontSize: '.82rem' }}>Attachments</Typography>
            <Stack direction="row" spacing={1}>
              <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} onChange={onFilesSelected} />
              <Button variant="contained" onClick={onUploadClick} disabled={uploading || loading}>Upload</Button>
              <Button variant="outlined" onClick={refreshAttachments} disabled={loading}>Refresh</Button>
            </Stack>
          </Stack>
          <TableContainer sx={{ overflowX: 'auto', mb: 2 }}><Table size="small" sx={{ minWidth: 760 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600 }}>File Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Size</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Uploaded By</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Uploaded Date</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(attachments || []).map((a: any, i: number) => (
                <TableRow key={Number(a.attachmentid ?? (a as any).AttachmentID ?? i)}>
                  <TableCell>{a.filename}</TableCell>
                  <TableCell>{Number(a.filesize || 0)}</TableCell>
                  <TableCell>{a.filetype}</TableCell>
                  <TableCell>{a.uploadedbyname || ''}</TableCell>
                  <TableCell>{a.uploadeddate || ''}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button size="small" href={apiService.getAttachmentViewUrl(Number(a.attachmentid))} target="_blank">View</Button>
                      <Button size="small" href={apiService.getAttachmentDownloadUrl(Number(a.attachmentid))}>Download</Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {(!attachments || !attachments.length) && (
                <TableRow>
                  <TableCell colSpan={6}>No attachments</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table></TableContainer>

          {poRelationRows.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700, color: PR_CLASSIC.blueDark, borderLeft: `3px solid ${PR_CLASSIC.blue}`, pl: 1, textTransform: 'uppercase', letterSpacing: '.035em', fontSize: '.82rem' }}>PO Relations</Typography>
              <TableContainer sx={{ overflowX: 'auto', mb: 2 }}><Table size="small" sx={{ minWidth: 900 }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600 }}>PO</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Vendor</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>SAP Doc</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Reference Type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Created At</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {poRelationRows.map((p: any, i: number) => (
                    <TableRow key={`${p.poid || p.docnum || i}`}>
                      <TableCell>
                        {Number(p.poid || 0) > 0 ? (
                          <Button size="small" variant="text" onClick={() => navigate(`/po/${Number(p.poid)}`)}>
                            {String(p.ponumber || '').trim() || `PO #${Number(p.poid)}`}
                          </Button>
                        ) : (
                          String(p.ponumber || '').trim() || '-'
                        )}
                      </TableCell>
                      <TableCell>{p.postatus || '-'}</TableCell>
                      <TableCell>{p.vendorname || p.vendorcode || '-'}</TableCell>
                      <TableCell>{Number(p.pototalamount || 0).toFixed(2)} {p.pocurrency || (data as any).currency || ''}</TableCell>
                      <TableCell>{(p.docnum || p.docentry) ? `DocNum ${p.docnum || '-'} / DocEntry ${p.docentry || '-'}` : '-'}</TableCell>
                      <TableCell>{p.reftype || '-'}</TableCell>
                      <TableCell>{toDDMMYYYY(p.createdat)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table></TableContainer>
            </>
          )}

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700, color: PR_CLASSIC.blueDark, borderLeft: `3px solid ${PR_CLASSIC.blue}`, pl: 1, textTransform: 'uppercase', letterSpacing: '.035em', fontSize: '.82rem' }}>PR SAP Details</Typography>
          <Stack spacing={0.5} mb={2}>
            {prSapRows.length ? (
              <>
                {prSapRows.map((p: any, i: number) => (
                  <Typography key={i} variant="body2">
                    {String(p.ponumber || '').trim() || 'SAP PR'}:
                    {' '}DocNum {p.docnum || ''} / DocEntry {p.docentry || ''}
                  </Typography>
                ))}
              </>
            ) : ((data as any).sappodocentry || (data as any).sappodocnum) ? (
              <Typography variant="body2">DocNum {(data as any).sappodocnum || ''} / DocEntry {(data as any).sappodocentry || ''}</Typography>
            ) : (
              <Typography variant="body2">Not yet converted to SAP PO</Typography>
            )}
          </Stack>

          {(function(){ const role = String(user?.role || ''); const allowedModules: string[] = (((user as any)?.allowedmodules ?? []) as string[]); const canSeeSAPDebug = role === 'Admin' || role === 'ProcurementOfficer' || allowedModules.includes('post-sap'); return (canSeeSAPDebug && Array.isArray(sapPostDebugAttempts) && sapPostDebugAttempts.length > 0); })() && (
            <>
              <Divider sx={{ my: 2 }} />
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700, color: PR_CLASSIC.blueDark, borderLeft: `3px solid ${PR_CLASSIC.blue}`, pl: 1, textTransform: 'uppercase', letterSpacing: '.035em', fontSize: '.82rem' }}>SAP Posting Debug</Typography>
                <Button variant="text" onClick={() => setDebugPanelOpen((v) => !v)}>
                  {debugPanelOpen ? 'Hide' : 'Show'} ({sapPostDebugAttempts.length})
                </Button>
              </Stack>
              <Collapse in={debugPanelOpen}>
                <Stack spacing={1} sx={{ mt: 1 }}>
                  {sapPostDebugAttempts.map((att: any, idx: number) => (
                    <Box key={idx} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                      <Typography variant="body2">Attempt {idx + 1}: {String(att.endpoint || '')} {att.vendorCode ? `(Vendor ${String(att.vendorCode)})` : ''}</Typography>
                      <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12, background: '#f6f8fa', padding: 12, borderRadius: 6 }}>
{JSON.stringify(att.payload ?? att, null, 2)}
                      </pre>
                      {(att.response || att.result) ? (
                        <>
                          <Typography variant="body2">Service Layer Response</Typography>
                          <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12, background: '#f6f8fa', padding: 12, borderRadius: 6 }}>
{JSON.stringify(att.response ?? att.result, null, 2)}
                          </pre>
                        </>
                      ) : null}
                      {att.errorResponse ? (
                        <>
                          <Typography variant="body2" color="error">Service Layer Error</Typography>
                          <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12, background: '#fff5f5', padding: 12, borderRadius: 6 }}>
{JSON.stringify(att.errorResponse, null, 2)}
                          </pre>
                        </>
                      ) : null}
                    </Box>
                  ))}
                </Stack>
              </Collapse>
            </>
          )}

          {(function(){ const role = String(user?.role || ''); const allowedModules: string[] = (((user as any)?.allowedmodules ?? []) as string[]); const canSeeHistory = role === 'Admin' || allowedModules.includes('sap-posting-history'); return (canSeeHistory && Array.isArray(postingHistory) && postingHistory.length > 0); })() && (
            <>
              <Divider sx={{ my: 2 }} />
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700, color: PR_CLASSIC.blueDark, borderLeft: `3px solid ${PR_CLASSIC.blue}`, pl: 1, textTransform: 'uppercase', letterSpacing: '.035em', fontSize: '.82rem' }}>SAP Posting History</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button variant="outlined" size="small" onClick={async () => {
                    if (!id) return;
                    try {
                      const histRes = await apiService.getPRPostingAttempts(Number(id));
                      const hist = (histRes as any)?.data || histRes || [];
                      setPostingHistory(Array.isArray(hist) ? hist : []);
                    } catch {}
                  }}>Refresh</Button>
                  <Button variant="text" onClick={() => setHistoryOpen((v) => !v)}>
                    {historyOpen ? 'Hide' : 'Show'} ({postingHistory.length} runs)
                  </Button>
                </Stack>
              </Stack>
              <Collapse in={historyOpen}>
                <Stack spacing={2} sx={{ mt: 1 }}>
                  {postingHistory.map((grp: any, gidx: number) => (
                    <Box key={String(grp.batchId || gidx)} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                      <Typography variant="body2">Batch {String(grp.batchId || '')} • {String(grp.postedAt || '')}</Typography>
                      <Stack spacing={1} sx={{ mt: 1 }}>
                        {(grp.attempts || []).map((att: any, idx: number) => (
                          <Box key={idx} sx={{ border: '1px dashed #e0e0e0', borderRadius: 1, p: 1 }}>
                            <Typography variant="body2">Attempt {att.attemptNo || idx + 1}: {String(att.endpoint || '')} {att.vendorCode ? `(Vendor ${String(att.vendorCode)})` : ''} {att.outcome ? `• ${String(att.outcome)}` : ''}</Typography>
                            {att.errorMessage ? (
                              <Typography variant="caption" color="error">{String(att.errorMessage)}</Typography>
                            ) : null}
                            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12, background: '#f6f8fa', padding: 12, borderRadius: 6 }}>
{JSON.stringify(att.payload ?? {}, null, 2)}
                            </pre>
                            {att.response ? (
                              <>
                                <Typography variant="body2">Service Layer Response</Typography>
                                <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12, background: '#f6f8fa', padding: 12, borderRadius: 6 }}>
{JSON.stringify(att.response, null, 2)}
                                </pre>
                              </>
                            ) : null}
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </Collapse>
            </>
          )}

          {/* Budget Information Section */}
          {Array.isArray((data as any)?.budgetInfo) && (data as any).budgetInfo.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography id="pr-b1-budget" variant="subtitle1" gutterBottom sx={{ fontWeight: 700, color: PR_CLASSIC.blueDark, borderLeft: `3px solid ${PR_CLASSIC.blue}`, pl: 1, scrollMarginTop: 72 }}>
                Budget Information
              </Typography>
              <Table size="small" sx={{ mb: 2 }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Fiscal Year</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Month</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Department</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>WBS</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Allocated YTD</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Consumed YTD</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Available</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Available (w/ Tolerance)</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>This PR</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {((data as any).budgetInfo as any[]).map((budget: any, idx: number) => {
                    const statusColor =
                      budget.status === 'within_budget' ? '#2e7d32' :
                      budget.status === 'within_tolerance' ? '#ed6c02' :
                      budget.status === 'exceeded' ? '#d32f2f' :
                      '#666';
                    const statusText =
                      budget.status === 'within_budget' ? 'Within Budget' :
                      budget.status === 'within_tolerance' ? 'Within Tolerance' :
                      budget.status === 'exceeded' ? 'Budget Exceeded' :
                      budget.status === 'no_budget' ? 'No Budget Defined' :
                      budget.status;

                    return (
                      <TableRow key={idx}>
                        <TableCell>
                          {budget.fiscalYear || '-'}
                        </TableCell>
                        <TableCell>
                          {budget.month || '-'}
                        </TableCell>
                        <TableCell>
                          {budget.departmentName || budget.department || '-'}
                        </TableCell>
                        <TableCell>
                          {budget.wbsName || budget.wbs || '-'}
                        </TableCell>
                        <TableCell align="right">
                          {budget.allocatedYTD !== undefined ? Number(budget.allocatedYTD).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                        </TableCell>
                        <TableCell align="right">
                          {budget.consumedYTD !== undefined ? Number(budget.consumedYTD).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                        </TableCell>
                        <TableCell align="right" sx={{ color: budget.availableYTD < 0 ? '#d32f2f' : '#2e7d32', fontWeight: 500 }}>
                          {budget.availableYTD !== undefined ? Number(budget.availableYTD).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                        </TableCell>
                        <TableCell align="right" sx={{ color: budget.availableWithTolerance < 0 ? '#d32f2f' : '#2e7d32', fontWeight: 500 }}>
                          {budget.availableWithTolerance !== undefined ? Number(budget.availableWithTolerance).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                          {budget.tolerancePercent !== undefined && ` (${budget.tolerancePercent}%)`}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 500 }}>
                          {budget.thisPRAmount !== undefined ? Number(budget.thisPRAmount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                        </TableCell>
                        <TableCell align="center">
                          <Box
                            sx={{
                              display: 'inline-block',
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 1,
                              backgroundColor: statusColor + '20',
                              color: statusColor,
                              fontWeight: 600,
                              fontSize: '0.75rem'
                            }}
                          >
                            {statusText}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {((data as any).budgetInfo as any[]).some((b: any) => b.message) && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                  {((data as any).budgetInfo as any[]).filter((b: any) => b.message).map((b: any, i: number) => (
                    <span key={i}>{b.message}<br /></span>
                  ))}
                </Typography>
              )}
            </>
          )}

          <Divider sx={{ my: 2 }} />
          <Box id="pr-b1-lines" sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-end', gap: 1, mb: 1, scrollMarginTop: 72 }}>
            <Box sx={{ borderLeft: `3px solid ${PR_CLASSIC.blue}`, pl: 1 }}><Typography variant="subtitle1" sx={{ fontWeight: 700, color: PR_CLASSIC.blueDark, textTransform: 'uppercase', letterSpacing: '.035em', fontSize: '.82rem' }}>PR Lines</Typography><Typography variant="body2" color="text.secondary">Project, service values, quantities and dimensions for each line.</Typography></Box>
            {String(data.allocationtype || '').toLowerCase() === 'project' && <Chip variant="outlined" label={`Project · ${(data as any).allocationDetails?.code || data.allocationcode || data.lines?.find((line: any) => line.projectcode || line.ProjectCode)?.projectcode || data.lines?.find((line: any) => line.projectcode || line.ProjectCode)?.ProjectCode || '-'}`} sx={{ maxWidth: '100%', borderColor: '#d8bd62', color: '#705900', bgcolor: PR_CLASSIC.yellowPale, borderRadius: '3px', fontWeight: 700 }} />}
          </Box>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, width: '100%', maxWidth: '100%', overflowX: 'auto', borderRadius: 2 }}>
          <Table size="small" stickyHeader sx={{ minWidth: 1270 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Item/Description</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Vendor</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Warehouse</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Project Code / Name</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>{isServiceModule ? 'Service Qty' : 'Qty'}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Closed Qty</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Open Qty</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>UoM</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Delivery Date</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>{isServiceModule ? 'Service Unit Price' : 'Unit Price'}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Line Total</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Division</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Department</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Cost Code (WBS)</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Auxiliary</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>LOC</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Line Remarks</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(Array.isArray(data.lines) ? data.lines : []).map((ln: any, idx: number) => (
                <TableRow key={idx}>
                  <TableCell>{idx + 1}</TableCell>
                  <TableCell sx={{ minWidth: 220, whiteSpace: 'pre-wrap' }}>
                    {ln.itemcode && <Typography variant="body2" fontWeight={600}>{ln.itemcode}</Typography>}
                    <Typography variant="body2">{ln.itemdescription || '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    {ln.vendorcode && ln.vendorname
                      ? `${ln.vendorcode} - ${ln.vendorname}`
                      : (ln.vendorcode || ln.vendorname || '')}
                  </TableCell>
                  <TableCell>{ln.warehouse || ln.warehousecode || ln.warehousename || ''}</TableCell>
                  <TableCell>{ln.projectcode || ln.ProjectCode || (String(data.allocationtype || '').toLowerCase() === 'project' ? ((data as any).allocationDetails?.code || data.allocationcode) : '') || ''}{ln.projectname ? <Typography variant="caption" display="block" color="text.secondary">{ln.projectname}</Typography> : null}</TableCell>
                  <TableCell align="right">{prLineValues(data.requisitiontype, ln).quantity}</TableCell>
                  <TableCell align="right">{Number(ln.closedqty || 0).toFixed(4)}</TableCell>
                  <TableCell align="right">{(isServiceModule ? Math.max(0, prLineValues(data.requisitiontype, ln).quantity - Number(ln.closedqty || 0)) : Number(ln.openqty ?? ln.quantity ?? 0)).toFixed(4)}</TableCell>
                  <TableCell align="right">{ln.uomcode || ''}</TableCell>
                  <TableCell align="right">{toDDMMYYYY(ln.deliverydate)}</TableCell>
                  <TableCell align="right">{prLineValues(data.requisitiontype, ln).unitPrice.toFixed(2)}</TableCell>
                  <TableCell align="right">{prLineValues(data.requisitiontype, ln).amount.toFixed(2)}</TableCell>
                  <TableCell align="right">{ln.dim1 || ''}</TableCell>
                  <TableCell align="right">{ln.dim2 || ''}</TableCell>
                  <TableCell align="right">{ln.dim3 || ''}</TableCell>
                  <TableCell align="right">{ln.dim4 || ''}</TableCell>
                  <TableCell align="right">{ln.dim5 || ''}</TableCell>
                  <TableCell sx={{ whiteSpace: 'pre-wrap', minWidth: 220 }}>{ln.u_item_details || ln.lineremarks || ln.LineRemarks || ln.lineRemarks || ln.u_remarks || ln.U_Remarks || '—'}</TableCell>
                </TableRow>
              ))}
              {(!data.lines || !data.lines.length) && (
                <TableRow>
                  <TableCell colSpan={18}>No lines</TableCell>
                </TableRow>
              )}
              {(Array.isArray(data.lines) && data.lines.length > 0) && (
                <TableRow sx={{ backgroundColor: '#f5f5f5', fontWeight: 600 }}>
                  <TableCell colSpan={5} sx={{ fontWeight: 600 }}>Total</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {data.lines.reduce((sum: number, ln: any) => sum + prLineValues(data.requisitiontype, ln).quantity, 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </TableCell>
                  <TableCell colSpan={5}></TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {data.lines.reduce((sum: number, ln: any) => sum + prLineValues(data.requisitiontype, ln).amount, 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </TableCell>
                  <TableCell colSpan={6}></TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          </TableContainer>
          <Divider sx={{ my: 2 }} />
          <Stack id="pr-b1-gl" direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1} sx={{ mb: 1.25, scrollMarginTop: 72 }}>
            <Box sx={{ borderLeft: `3px solid ${PR_CLASSIC.blue}`, pl: 1 }}><Typography variant="subtitle1" sx={{ fontWeight: 700, color: PR_CLASSIC.blueDark, textTransform: 'uppercase', letterSpacing: '.035em', fontSize: '.82rem' }}>G/L Impact Details</Typography><Typography variant="body2" color="text.secondary">Estimated debit and credit accounts for each line, from this PR and the synced SAP mapping.</Typography></Box>
            {data.glImpactPreview?.available && <Paper variant="outlined" sx={{ px: 2, py: 1, borderRadius: 1, borderColor: '#d8bd62', borderLeft: `4px solid ${PR_CLASSIC.yellow}`, bgcolor: PR_CLASSIC.yellowPale }}>
              <Typography variant="caption" color="text.secondary" display="block">Estimated total · {data.currency || ''}</Typography>
              <Typography variant="h6" fontWeight={800} color="#705900">{Number(data.glImpactPreview.totalAmount ?? (data.glImpactPreview.items || []).reduce((sum: number, item: any) => sum + Number(item.amount || 0), 0)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
            </Paper>}
          </Stack>
          {data.glImpactPreview?.available ? (
            <>
              <Alert severity="info" sx={{ mb: 1.5, borderRadius: 2 }}>
              When a line has both service values, the preview uses <strong>U_Ser_Qty × U_Ser_Unit_Price</strong>. If either value is blank, it uses the PR line value. SAP posting may also include tax, freight, exchange rates, valuation, or posting-date effects.
              </Alert>
              {(Array.isArray(data.glImpactPreview.warnings) ? data.glImpactPreview.warnings : []).map((warning: string, i: number) => (
                <Alert key={i} severity="warning" sx={{ mb: 1.5, borderRadius: 2 }}>{warning}</Alert>
              ))}
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 1050 }}>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f7fa' }}>
                      <TableCell rowSpan={2} sx={{ fontWeight: 700 }}>PR Line</TableCell>
                      <TableCell rowSpan={2} sx={{ fontWeight: 700 }}>Mapping Rule</TableCell>
                      <TableCell colSpan={2} align="center" sx={{ fontWeight: 700 }}>Goods Receipt (GRPO)</TableCell>
                      <TableCell colSpan={2} align="center" sx={{ fontWeight: 700 }}>Goods Issue</TableCell>
                      <TableCell colSpan={2} align="center" sx={{ fontWeight: 700 }}>A/P Invoice</TableCell>
                    </TableRow>
                    <TableRow sx={{ backgroundColor: '#fafafa' }}>
                      {['Debit', 'Credit', 'Debit', 'Credit', 'Debit', 'Credit'].map((label, i) => <TableCell key={i} sx={{ fontWeight: 600 }}>{label}</TableCell>)}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(data.glImpactPreview.items || []).map((impact: any, i: number) => {
                      const showAccount = (value: any) => value?.displayName
                        ? <>{value.displayName}<Typography component="div" variant="caption" color="text.secondary">{Number(value.amount || 0).toFixed(2)} {data.currency || ''}</Typography></>
                        : value?.code
                        ? <>{value.code}{value.name ? ` — ${value.name}` : ''}<Typography component="div" variant="caption" color="text.secondary">{Number(value.amount || 0).toFixed(2)} {data.currency || ''}</Typography></>
                        : <Typography variant="caption" color="text.secondary">{value?.unresolvedReason || 'Not resolved from synced mapping'}</Typography>;
                      return <TableRow key={`${impact.lineNumber}-${i}`}>
                        <TableCell sx={{ minWidth: 200 }}>Line {impact.lineNumber}{impact.itemCode ? `: ${impact.itemCode}` : impact.description ? '' : ': Service line'}{impact.description && <Typography component="div" variant="body2" sx={{ maxWidth: 280, whiteSpace: 'pre-wrap' }}>{impact.description}</Typography>}<Typography component="div" variant="caption" color="text.secondary">{impact.amountSource === 'service' ? `Service: ${Number(impact.serviceQty || 0)} × ${Number(impact.serviceUnitPrice || 0).toFixed(2)} = ` : ''}{Number(impact.amount || 0).toFixed(2)} {data.currency || ''}</Typography></TableCell>
                        <TableCell>{impact.ruleCode || <Typography variant="caption" color="warning.main">No matching rule</Typography>}</TableCell>
                        <TableCell>{showAccount(impact.scenarios?.grpo?.debit)}</TableCell>
                        <TableCell>{showAccount(impact.scenarios?.grpo?.credit)}</TableCell>
                        <TableCell>{showAccount(impact.scenarios?.goodsIssue?.debit)}</TableCell>
                        <TableCell>{showAccount(impact.scenarios?.goodsIssue?.credit)}</TableCell>
                        <TableCell>{showAccount(impact.scenarios?.apInvoice?.debit)}</TableCell>
                        <TableCell>{showAccount(impact.scenarios?.apInvoice?.credit)}</TableCell>
                      </TableRow>;
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : <Alert severity="info" sx={{ mb: 2 }}>The full SAP G/L account mapping has not been synced for this company. Run the G/L Account Advanced Rules sync to view an estimated impact.</Alert>}
        </Box>
      )}
    </Paper>
    <Backdrop open={!!actionProcessing} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
      <Stack spacing={2} alignItems="center">
        <CircularProgress color="inherit" />
        <Typography variant="body1">
          {actionProcessing === 'approve' ? 'Processing approval. Please wait…' : actionProcessing === 'reject' ? 'Processing rejection. Please wait…' : ''}
        </Typography>
        <Typography variant="body2">Do not close or click Approve/Reject until this finishes.</Typography>
      </Stack>
    </Backdrop>
    <Backdrop open={poProcessing} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 2 }}>
      <Stack spacing={2} alignItems="center">
        <CircularProgress color="inherit" />
        <Typography variant="body1">Posting to SAP…</Typography>
        <Typography variant="body2">This may take a few seconds depending on SAP workload.</Typography>
      </Stack>
    </Backdrop>
    <Dialog open={debugOpen} onClose={() => setDebugOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle>Purchase Request Payload (Admin)</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" gutterBottom>
          Endpoint: {String((sapErrorDebug as any)?.endpoint || '/PurchaseRequests')}
        </Typography>
        <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12, background: '#f6f8fa', padding: 12, borderRadius: 6 }}>
{JSON.stringify(sapErrorDebug, null, 2)}
        </pre>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDebugOpen(false)}>Close</Button>
      </DialogActions>
    </Dialog>
    <Backdrop open={navigating} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 2 }}>
      <Stack spacing={2} alignItems="center">
        <CircularProgress color="inherit" />
        <Typography variant="body1">Navigating…</Typography>
      </Stack>
    </Backdrop>
    </>
  );
};

export default PRDetail;
